import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { cameraService } from '../../../services/central-api/cameras';
import moment from 'moment';
import { View } from 'react-native';
import BasicModal from '../../../components/BasicModal';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import FormInput from '../../../components/FormInput';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    cameraId?: string;
}

const regex = /^[a-zA-Z0-9 +\-./:=_ãõẽíáàâêóôúüçÃÕẼÍÁÀÂÊÓÔÚÜÇ]*$/;

export default function DownloadModal({ cameraId, isModalVisible, setModalVisible }: ModalProps) {
    const [startInDateFilter, setStartInDateFilter] = useState<Date | null>(null);
    const [startInHourFilter, setStartInHourFilter] = useState('00:00');
    const [endInDateFilter, setEndInDateFilter] = useState<Date | null>(null);
    const [endInHourFilter, setEndInHourFilter] = useState('00:00');
    const [downloadName, setDownloadName] = useState<string>('');
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [startInZIndex, setStartInZIndex] = useState<number>(16);
    const [endInZIndex, setEndInZIndex] = useState<number>(15);

    const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>([]);
    const [selectableTags, setSelectableTags] = useState<{ label: string; value: number; }[]>([]);

    function closeDownloadModal() {
        setModalVisible(false);
        resetDownloadForm();
    }

    function resetDownloadForm() {
        setDownloadName('');
        setSelectedTagsIds([]);
        setStartInDateFilter(null);
        setStartInHourFilter('00:00');
        setEndInDateFilter(null);
        setEndInHourFilter('00:00');
    }

    async function getDownloadTags() {
        try {
            const tags = await cameraService.getSimplifiedDownloadTags();
            setSelectableTags(tags.map((tag) => {
                return {
                    value: tag.id,
                    label: tag.name
                };
            }));
        } catch (err) {
            console.error(err);
        }
    }

    function getDates() {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(startInDateFilter, startInHourFilter ? `${startInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        return { beginIn, endIn };
    }

    function isDownloadFormValid() {
        const { beginIn, endIn } = getDates();

        return downloadName !== '' &&
            regex.test(downloadName) &&
            beginIn &&
            endIn;
    }

    async function createDownload(): Promise<void | false> {
        try {
            if (!cameraId) {
                return false;
            }
            setIsDownloading(true);

            const { beginIn, endIn } = getDates();

            await cameraService.createCameraDownload({
                cameraId: cameraId,
                name: downloadName,
                msStart: beginIn as number,
                msFinish: endIn as number,
                tags: selectedTagsIds
            });
            closeDownloadModal();
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('downloadStarted'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
                return false;
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        } finally {
            setIsDownloading(false);
        }
    }

    function validateDownloadName(value: string) {
        if (value == '') {
            return true;
        }
        return !regex.test(value);
    }

    useEffect(() => {
        if (isModalVisible) {
            getDownloadTags();
        }
    }, [isModalVisible]);

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={translate('downloadVideo')}
            width='small'
            mobileHeight='fit'
            cancelButton={{ onClick: closeDownloadModal }}
            primaryButton={{
                text: translate('save'),
                onClick: createDownload,
                disabled: !isDownloadFormValid() || isDownloading,
            }}
            disableScrollView={true}
        >
            <FormInput
                label={translate('name')}
                placeholder={translate('name')}
                value={downloadName}
                onChangeText={setDownloadName}
                invalid={validateDownloadName}
                errorMessage={translate('invalidCharacters')}
            />
            <View style={{ zIndex: 2 }}>
                <FilterDropdown
                    label={translate('tags')}
                    items={selectableTags}
                    multiple={true}
                    value={selectedTagsIds}
                    setValue={setSelectedTagsIds}
                    mode='BADGE'
                />
            </View>
            <View style={{ flexDirection: 'column', rowGap: 16, flexWrap: 'wrap', zIndex: (endInZIndex != 15 || startInZIndex != 16) ? 3 : 1, minWidth: 300 }}>
                <FilterDatePicker
                    date={startInDateFilter}
                    onChangeDate={setStartInDateFilter}
                    time={startInHourFilter}
                    onChangeTime={setStartInHourFilter}
                    label={translate('beginIn')}
                    zIndex={startInZIndex}
                    onCalendarOpen={() => setStartInZIndex(9999)}
                    onCalendarClose={() => setStartInZIndex(16)}
                />
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('endIn')}
                    zIndex={endInZIndex}
                    onCalendarOpen={() => setEndInZIndex(9999)}
                    onCalendarClose={() => setEndInZIndex(15)}
                />
            </View>
        </BasicModal>);
}
